// Unpublished Work © 2023-2024 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import Stack from '@mui/material/Stack';
import {IconDelete, IconHandleChevronDown, IconHandleChevronUp} from '@deere/icons';
import CollapsedWorkItemInput from 'Ui/features/workboard-wizard/work-item/collapsed-work-item-input';
import WorkItemDragHandle from 'Ui/features/workboard-wizard/work-item/work-item-drag-handle';
import WorkItemInput from 'Ui/features/workboard-wizard/work-item/work-item-input';
import OnlinkButton from 'Ui/components/common/onlink-button';
import {useDeepMemo} from 'Utils/react-utils';

const ICON_STYLE = {
    style: {
        height: '20px',
        width: '20px'
    }
};

function WorkItemInputWrapper(props) {
    const {
        collapsedTitle,
        collapseStatusForWorkItem,
        deleteTitle,
        disabledClear,
        onRemove,
        setStatusForWorkItem,
        translations,
        workItem,
        readOnly,
        ...otherProps
    } = props;

    const collapseButton = React.useMemo(() => {
        const collapseIcon = collapseStatusForWorkItem ?
            <IconHandleChevronDown {...ICON_STYLE}/> :
            <IconHandleChevronUp {...ICON_STYLE}/>;

        return (
            <Stack marginLeft='auto'>
                <OnlinkButton
                    borderless={true}
                    className='action-button'
                    onClick={setStatusForWorkItem}
                    startIcon={collapseIcon}
                    variant='borderless'
                />
            </Stack>
        );
    }, [collapseStatusForWorkItem]);

    const deleteIcon = React.useMemo(() => (
        <IconDelete
            {...ICON_STYLE}
        />
    ), []);

    const deleteButton = useDeepMemo(() => (
        <Stack
            sx={{
                alignSelf: 'flex-end'
            }}
        >
            <OnlinkButton
                borderless={true}
                className='action-button assignment-item-btn icon-and-text'
                disabled={disabledClear}
                onClick={onRemove}
                startIcon={deleteIcon}
                variant={'borderless'}
            >
                {deleteTitle}
            </OnlinkButton>
        </Stack>
    ), [deleteTitle, disabledClear, onRemove]);

    const orderButton = React.useMemo(() => !readOnly && (
        <WorkItemDragHandle
            className='icon-workitem-orderable assignment-item-btn'
            id={workItem.workItemId}
        />
    ), [readOnly, workItem.workItemId]);

    if (collapseStatusForWorkItem) {
        return (
            <CollapsedWorkItemInput
                collapseButton={collapseButton}
                collapsedTitle={collapsedTitle}
                equipment={workItem.equipment}
                orderButton={orderButton}
                translations={translations}
                workItemId={workItem.workItemId}
            />
        );
    }

    return (
        <WorkItemInput
            {...otherProps}
            collapseButton={collapseButton}
            deleteButton={deleteButton}
            orderButton={orderButton}
            readOnly={readOnly}
            translations={translations}
            workItem={workItem}
        />
    );
}

WorkItemInputWrapper.propTypes = {
    children: PropTypes.node,
    collapsedTitle: PropTypes.string,
    collapseStatusForWorkItem: PropTypes.bool,
    deleteTitle: PropTypes.string,
    disabledClear: PropTypes.bool,
    eligibleEquipmentTypes: PropTypes.arrayOf(PropTypes.object),
    equipmentAreaId: PropTypes.string,
    equipmentByType: PropTypes.instanceOf(Map),
    equipmentInUseMap: PropTypes.reference,
    onActHoursChange: PropTypes.func,
    onChange: PropTypes.func,
    onEquipmentChange: PropTypes.func,
    onEstHoursChange: PropTypes.func,
    onRemove: PropTypes.func,
    readOnly: PropTypes.bool,
    setStatusForWorkItem: PropTypes.func,
    translations: PropTypes.translations,
    workItem: PropTypes.object
};

export default WorkItemInputWrapper;
